import { Component, DestroyRef, EventEmitter, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { CompanyOrderInfo } from '../../models/types/company-order-info.type';
import { UtilitiesService } from 'src/app/shared/services/utilities.service';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { AppService } from 'src/app/app.service';
import { MutationQuery } from 'src/app/models/shared/enums/mutation-query.enum';
import { parseZone } from 'moment';
import { Selectable } from 'src/app/shared/models/types/selectable.type';
import { Childable } from 'src/app/shared/models/types/childable.type';
import { CompanyStyle } from '../../models/company-style.type';
import { StyleColorShot } from '../../models/types/style-color-shot.type';
import { ModelColorSize } from 'src/app/models/photographer/create-order.model';

export type NewOrderOutletEvent = {
	type: string;
	payload?: unknown;
}

@Component({
	standalone: false,
	selector: 'bt-new-company-order',
	templateUrl: './new-company-order.component.html',
	styleUrls: ['./new-company-order.component.scss']
})
export class NewCompanyOrderComponent implements OnInit {
	activeSubRoute: string;

	readonly #destroyRef = inject(DestroyRef);
	readonly #router = inject(Router);
	readonly #utilitiesService = inject(UtilitiesService);
	readonly #graphQLService = inject(GraphQLService);
	readonly #appService = inject(AppService);

	tabs = [
		{ id: 'orderInfo', title: 'Order Info', url: 'order-detail-info' },
		{ id: 'orderDetails', title: 'Order Details', url: 'order-detail-shots' }
	];

	orderInfo: CompanyOrderInfo = null;
	orderDetails: Array<Selectable<Childable<CompanyStyle, Selectable<StyleColorShot>>>> = [];
	warehouseId: number = 0;

	constructor() {
		this.#router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntilDestroyed(this.#destroyRef)
			)
			.subscribe(() => {
				this.updateRouteInfo();
			});
	}

	ngOnInit(): void {
		this.updateRouteInfo();
	}

	private updateRouteInfo(): void {
		const url = this.#router.url;
		const urlSegments = url.split('/');

		const orderInfoIndex = urlSegments.indexOf('new-order');
		if (orderInfoIndex !== -1 && urlSegments.length > orderInfoIndex + 1) {
			this.activeSubRoute = urlSegments[orderInfoIndex + 1];
		}
	}

	onBack() {
		this.#router.navigate(['/company/order-list']);
	}

	// selectTab(tabUrl: string): void {
	// 	this.#router.navigate(['/company/new-order', tabUrl]);
	// }

	/** Gestione generica: al caricamento del componente nel router-outlet ci sottoscriviamo ai suoi eventi. */
	onOutletActivate(component: { outletEvent?: EventEmitter<NewOrderOutletEvent> }): void {
		if (component?.outletEvent) {
			component.outletEvent
				.pipe(takeUntilDestroyed(this.#destroyRef))
				.subscribe((event: NewOrderOutletEvent) => {
					this.handleOutletEvent(event)
				});
		}
	}

	private handleOutletEvent(event: NewOrderOutletEvent): void {
		switch (event.type) {
			case 'savedOrderInfo':
				this.orderInfo = event.payload as CompanyOrderInfo;
				this.#router.navigate(['/company/new-order/order-detail-shots']);
				break;

			case 'savedOrderDetails':
				this.orderDetails = (event.payload as any).styleColors as Array<Selectable<Childable<CompanyStyle, Selectable<StyleColorShot>>>>;
				this.warehouseId = (event.payload as any).warehouseId;
				this.createOrder();
				break;

			default:
				break;
		}
	}

	private createOrder() {
		if (this.orderDetails.length && this.orderInfo) {
			const data: Array<ModelColorSize> = [];

			this.orderDetails.forEach((s) => {
				s.children.forEach((c: StyleColorShot) =>
					c.shots.forEach((sh) => {
						data.push(
							{
								colorId: c.colorId,
								modelId: sh.modelId,
								sizeGroupId: sh.sizeGroupId,
								shotTypeId: sh.shotTypeId,
								aspectRatioId: sh.aspectRatioId,
								mediaFormatId: sh.mediaFormatId,
								codifiedUsedId: sh.codifiedUsedId
							});
					})
				);
			});

			if (data.length === 0) {
				this.#utilitiesService.showSnackbar('Missing fields');
				return;
			}
			const payload = {
				photographerId: this.orderInfo.photographerId,
				warehouseId: this.warehouseId,
				stylesColorsSizes: data,
				endDate: parseZone(this.orderInfo.dueDate).utc(true).toISOString(),
				companyId: this.#appService.companyId,
				comments: this.orderInfo.notes,
				userId: this.#appService.user.userId
			};

			// this.#graphQLService
			// 	.postData(MutationQuery.CREATE_ORDER, payload)
			// 	.pipe(takeUntilDestroyed(this.#destroyRef))
			// 	.subscribe({
			// 		next: (value: any) => {
			// 			this.#utilitiesService.showSnackbar(value.message);
			// 			this.#router.navigate(['/company/order-list']);
			// 		},
			// 		error: (err) => {
			// 			console.error(err);
			// 			this.#utilitiesService.showSnackbar(err);
			// 		}
			// 	});
		} else {
			this.#utilitiesService.showSnackbar('Missing fields');
		}
	}
}
